import React, { useMemo } from 'react';

import { faChevronRight, faHome } from '@fortawesome/pro-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { TagModel, WorklistViewModel } from 'models';

import { Button, ButtonVariants, Icon } from 'core/ui';

import { generateBreadcrumbs, shouldShowBreadcrumb } from '../services/worklistNavigationService';
import type { BreadcrumbItem } from '../services/worklistNavigationService';

interface PatientFilesBreadcrumbProps {
  patientId: number;
  hierarchicalPath?: string;
  mergedFlatTags?: TagModel[] | null;
  mergedTagNameToIdMap?: Map<string, number>;
  selectedWorklist?: WorklistViewModel | null;
  allWorklists?: WorklistViewModel[] | null;
  stackName?: string;
  onToggleSidebar?: () => void;
  isLoading?: boolean;
}

export const PatientFilesBreadcrumb: React.FC<PatientFilesBreadcrumbProps> = ({
  patientId,
  hierarchicalPath,
  mergedFlatTags,
  mergedTagNameToIdMap,
  selectedWorklist,
  allWorklists,
  stackName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbs = useMemo(() => {
    return generateBreadcrumbs({
      patientId,
      currentPathname: location.pathname,
      selectedWorklist,
      allWorklists,
      stackName,
      hierarchicalTags: null,
    });
  }, [patientId, selectedWorklist, allWorklists, stackName, location.pathname]);

  const handleBreadcrumbClick = (breadcrumb: BreadcrumbItem) => {
    // Don't navigate for virtual items or if already on the same path
    if (breadcrumb.isVirtual || breadcrumb.path === location.pathname) {
      return;
    }
    navigate(breadcrumb.path);
  };

  const isCurrentPath = (breadcrumbPath: string) => {
    return location.pathname === breadcrumbPath;
  };

  if (!shouldShowBreadcrumb(breadcrumbs)) {
    return null; // Don't show breadcrumb if only showing "All Files"
  }

  return (
    <BreadcrumbContainer>
      {breadcrumbs.map((breadcrumb, index) => (
        <React.Fragment key={breadcrumb.id || `item-${index}`}>
          {index > 0 && (
            <BreadcrumbSeparator>
              <Icon icon={faChevronRight} />
            </BreadcrumbSeparator>
          )}
          <BreadcrumbItem
            isActive={isCurrentPath(breadcrumb.path) || breadcrumb.isVirtual}
            disabled={isCurrentPath(breadcrumb.path) || breadcrumb.isVirtual}
            onClick={() => handleBreadcrumbClick(breadcrumb)}
          >
            {breadcrumb.isRoot && <Icon icon={faHome} style={{ marginRight: '0.25rem' }} />}
            {breadcrumb.name}
          </BreadcrumbItem>
        </React.Fragment>
      ))}
    </BreadcrumbContainer>
  );
};

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const BreadcrumbItem = styled.button<{ isActive?: boolean }>`
  background: none;
  border: none;
  color: ${({ theme, isActive }) => (isActive ? theme.colors.textPrimary : theme.colors.primary)};
  cursor: ${({ isActive }) => (isActive ? 'default' : 'pointer')};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
  }

  &:disabled {
    cursor: default;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8rem;
  margin: 0 0.25rem;
`;

export default PatientFilesBreadcrumb;
